import Service, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import { hashToQueryDates, maxDate, minDate, toQueryDate } from 'rise-ui/config/time-series/query-params.js';
import { formatDate } from 'rise-ui/utils/date.js';
import { filter } from 'rise-ui/utils/object.js';
import { call } from 'rise-ui/utils/function.js';
import routeInfoOfName from 'rise-ui/utils/route-info-of-name.js';
import { qp } from 'rise-ui/services/time-series/params.js';

const mapFrom = obj =>
  new Map(Object.keys(obj || {}).map(id => [id, obj[id].slice()]));

function mapToDates(min, max) {
  return date => {
    const isMin = date === minDate;
    const isMax = date === maxDate;
    return (date && typeof date === 'string' && !isMin && !isMax) ? date
      : formatDate(isMin ? min : isMax ? max : date, 'url');
  };
}

const mapToPojo = map => {
  let obj;
  if (map) {
    obj = {};
    map.forEach((v, k) => (obj[k] = v));
  }
  return obj;
};

const empty = (qps, qp) => {
  if (qp in qps) {
    const obj = qps[qp];
    qps[qp] = obj && Object.keys(obj).length ? obj : undefined
  }
};

/**
 * @name LocationId
 * @type string
 */
/**
 * @name ItemId
 * @type string
 */
/**
 * @name ModelRunId
 * @type string
 */
/**
 * @name SelectionLinkParams
 * @property {Set.<LocationId>} locations
 * @property {Set.<ItemId>} items
 * @property {Set.<ModelRunId>} modelRuns
 * @property {[Date]} defaultDateRanges
 * @property {Map.<ItemId,Date>} itemDateRanges
 * @property {Map.<ModelRunId,Date>} modelRunDateRanges
 */

/**
 * @name TimeSeriesLinkParams
 * @property {boolean} clearDates
 * @property {boolean} clearLocations
 * @property {boolean} clearSearch
 * @property {LocationId} toggleLocation
 * @property {ItemId} toggleItem
 * @property {ModelRunId} toggleModelRun
 *
 * @property {[Date]} defaultDateRanges
 * @property {Map.<ItemId,Date>} itemDateRanges
 * @property {Map.<ModelRunId,Date>} modelRunDateRanges
 */

export default class TimeSeriesRouteService extends Service {
  @service routeState;

  /**
   * Returns link-to current time-series route with optionally modified selection query params
   * @property {TimeSeriesRouteService.getLink}
   */
  @cached
  get getLink() {
    const { _store, _timeSeriesParams: tsParams } = this;
    const locationIds = tsParams.locations;
    const itemIds = tsParams.items;
    const modelRunIds = tsParams.modelRuns;
    const { defaultDates, itemDates, modelRunDates } = tsParams;

    const searchParams = this.routeState.queryForRoute('time-series.search');

    /**
     * @name TimeSeriesRouteService.getLink
     * @param {function(SelectionLinkParams):? SelectionLinkParams} [handler]
     * @return TransitionInterface
     */
    return handler => {
      let params = {
        locations: new Set(locationIds),
        items: new Set(itemIds),
        modelRuns: new Set(modelRunIds),
        defaultDateRanges: defaultDates.slice(),
        itemDateRanges: mapFrom(itemDates),
        modelRunDateRanges: mapFrom(modelRunDates),
        filter: mapFrom(searchParams.filter),
        scalars: new Map([
          [ 'order', searchParams.order ],
          [ 'search', searchParams.search ],
        ]),
      };

      const result = call(handler, params, params);
      let { locations, items, modelRuns, defaultDateRanges,
        itemDateRanges, modelRunDateRanges, filter, scalars,
      } = { ...params, ...(result ?? {}) };

      const validLocations = [];
      const validItems = [];
      const validModelRuns = [];
      const validItemDates = new Map;
      const validModelRunDates = new Map;

      locations.forEach(locationId => {
        const location = _store.peekRecord('location', locationId);
        if (!location) {
          return;
        }
        validLocations.push(locationId);
        location.catalogRecords.forEach(record => {
          record.items.forEach(item => {
            if (items.has(item.id)) {
              validItems.push(item.id);
              if (itemDateRanges.has(item.id)) {
                validItemDates.set(item.id, itemDateRanges.get(item.id));
              }
            }
            item.modelRuns.forEach(modelRun => {
              if (modelRuns.has(modelRun.id)) {
                validModelRuns.push(modelRun.id);
                if (modelRunDateRanges.has(modelRun.id)) {
                  validModelRunDates.set(modelRun.id, modelRunDateRanges.get(modelRun.id));
                }
              }
            });
          });
        });
      });

      locations = validLocations.length ? validLocations : undefined;
      items = validItems.length ? validItems : undefined;
      modelRuns = validModelRuns.length ? validModelRuns : undefined;

      defaultDateRanges = Array.isArray(defaultDateRanges) && defaultDateRanges.length
        ? defaultDateRanges.map(toQueryDate) : undefined;
      itemDateRanges = validItemDates.size
        ? hashToQueryDates(mapToPojo(validItemDates)) : undefined;
      modelRunDateRanges = validModelRunDates.size
        ? hashToQueryDates(mapToPojo(validModelRunDates)) : undefined;
      filter = filter.size ? mapToPojo(filter) : undefined;
      const queryParams = {
        [qp.locations]: locations,
        [qp.items]: items,
        [qp.modelRuns]: modelRuns,
        [qp.defaultDates]: defaultDateRanges,
        [qp.itemDates]: itemDateRanges,
        [qp.modelRunDates]: modelRunDateRanges,
        filter,
        order: scalars.get('order'),
        search: scalars.get('search'),
      };
      return this._routing.getTransitionInterface(
        this._transition?.to || this._routeName,
        queryParams
      );
    };
  }

  @tracked modelUpdate;
  modelReport() {
    this.modelUpdate = {};
  }

  @cached get params() {
    // Consume model report to keep links up to date with loaded records
    this.modelUpdate;
    this._transition;

    const { _store, _timeSeriesParams: tsParams } = this;

    // Filter IDs for all locations with actual records
    const locationIds = tsParams.locations
      .filter(id => id && _store.peekRecord('location', id));

    // Store IDs for items by location ID
    const locationItemIds = {};
    // Filter IDs for all items with actual records
    const itemIds = tsParams.items.filter(id => {
      const item = id && _store.peekRecord('item', id);
      let locationId;
      // Item is only valid if its location is also selected
      const isValid = item && locationIds
        .includes(locationId = item.get('catalogRecord.location.id'));
      if (isValid) {
        (locationItemIds[locationId] = locationItemIds[locationId] || []).push(id);
      }
      return isValid;
    });

    // Store IDs for items by location ID
    const itemModelRunIds = {};
    // Store IDs for model runs by location ID
    const locationModelRunIds = {};
    // Filter IDs for all model runs with actual records
    const modelRunIds = tsParams.modelRuns.filter(id => {
      const mr = id && _store.peekRecord('model-run', id);
      let locationId;
      // Model run is only valid if its location is also selected
      const isValid = mr && locationIds
        .includes(locationId = mr.get('item.catalogRecord.location.id'));
      if (isValid) {
        (locationModelRunIds[locationId] = locationModelRunIds[locationId] || []).push(id);
        (itemModelRunIds[locationId] = itemModelRunIds[locationId] || []).push(id);
      }
      return isValid;
    });

    // Filter item dates to those with selected items
    const itemDates = filter(tsParams.itemDates, ([ id ]) => id && itemIds.includes(id));
    // Filter model run dates to those with selected model runs
    const modelRunDates = filter(tsParams.modelRunDates, ([ id ]) => id && modelRunIds.includes(id));

    return {
      locationIds,
      locationItemIds,
      itemIds,
      itemModelRunIds,
      locationModelRunIds,
      modelRunIds,
      defaultDates: tsParams.defaultDates,
      itemDates,
      modelRunDates,
    };
  }

  @cached get isClean() {
    this._transition;
    const searchParams = this.routeState.queryForRoute('time-series.search');
    const { params } = this._timeSeriesParams;
    return !params[qp.locations]
      && !params[qp.items]
      && !params[qp.itemDates]
      && !params[qp.modelRuns]
      && !params[qp.modelRunDates]
      && !searchParams.filter
      && !searchParams.order
      && !searchParams.search;
  }

  @cached get isDateClean() {
    this._transition;
    const { params } = this._timeSeriesParams;
    return !params[qp.itemDates]
      && !params[qp.modelRuns]
      && !params[qp.modelRunDates];
  }

  /**
   * Returns link-to current time-series route with optionally modified selection query params
   * @property {TimeSeriesRouteService.getLinkToSelect}
   */
  @cached
  get getLinkToSelect() {
    const {
      locationIds,
      locationItemIds,
      itemIds,
      itemModelRunIds,
      locationModelRunIds,
      modelRunIds,
      defaultDates,
      itemDates,
      modelRunDates,
    } = this.params;

    const { to: routeName = this._routeName } = this._transition || {};

    /**
     * @name TimeSeriesRouteService.getLinkToSelect
     * @param {function(TimeSeriesLinkParams)} [handler]
     * @return TransitionInterface
     */
    return ({
      clearDates,
      clearDefaultDates,
      clearItemDates,
      clearModelRunDates,
      setDefaultDates,
      setItemDates,
      setModelRunDates,
      clearLocations,
      copyDefaultDates,
      addLocation,
      addItem,
      addModelRun,
      toggleLocation,
      toggleItem,
      toggleModelRun,
      route,
      queryParams,
    }) => {
      const qps = {};

      if (clearDefaultDates || clearDates || clearLocations) {
        if (clearDefaultDates || clearDates) {
          qps[qp.defaultDates] = undefined;
        }
        if (clearDates || clearLocations) {
          qps[qp.itemDates] = undefined;
          qps[qp.modelRunDates] = undefined;
        }
        if (clearLocations) {
          qps[qp.locations] = undefined;
          qps[qp.items] = undefined;
          qps[qp.modelRuns] = undefined;
        }
      }

      if (setDefaultDates) {
        qps[qp.defaultDates] = setDefaultDates;
      }

      if (clearItemDates) {
        qps[qp.itemDates] = filter(itemDates, ([ id ]) => id !== clearItemDates);
      }
      if (clearModelRunDates) {
        qps[qp.modelRunDates] = filter(modelRunDates, ([ id ]) => id !== clearModelRunDates);
      }

      if (toggleLocation) {
        if (locationIds.includes(toggleLocation)) {
          // If location already selected, remove it
          qps[qp.locations] = locationIds.filter(id => id !== toggleLocation);
          // And remove any associated items
          const items = locationItemIds[toggleLocation];
          if (items) {
            qps[qp.items] = itemIds.filter(id => !items.includes(id));
            // And remove any associated dates
            qps[qp.itemDates] = filter(itemDates, ([ id ]) => !items.includes(id));
          }
          // And remove any associated model runs
          const modelRuns = locationModelRunIds[toggleLocation];
          if (modelRuns) {
            qps[qp.modelRuns] = modelRunIds.filter(id => !modelRuns.includes(id));
            // And remove any associated dates
            qps[qp.modelRunDates] = filter(modelRunDates, ([ id ]) => !modelRuns.includes(id));
          }
        } else {
          addLocation = toggleLocation;
        }
      }

      if (toggleItem) {
        if (itemIds.includes(toggleItem)) {
          // If item already selected, remove it
          qps[qp.items] = itemIds.filter(id => id !== toggleItem);
          // And remove any associated dates
          qps[qp.itemDates] = filter(itemDates, ([ id ]) => id !== toggleItem);
          // And remove any associated model runs
          const modelRuns = itemModelRunIds[toggleItem];
          if (modelRuns) {
            qps[qp.modelRuns] = modelRunIds.filter(id => !modelRuns.includes(id));
            // And remove any associated dates
            qps[qp.modelRunDates] = filter(modelRunDates, ([ id ]) => !modelRuns.includes(id));
          }
        } else {
          // If item not selected, assume valid and select it
          addItem = toggleItem;
        }
      }

      if (toggleModelRun) {
        if (modelRunIds.includes(toggleModelRun)) {
          // If model run already selected, remove it
          qps[qp.modelRuns] = modelRunIds.filter(id => id !== toggleModelRun);
          // And remove any associated dates
          qps[qp.modelRunDates] = filter(modelRunDates, ([ id ]) => id !== toggleModelRun);
        } else {
          // If model run not selected, assume valid and select it
          addModelRun = toggleModelRun;
        }
      }

      if (addLocation) {
        if (!locationIds.includes(toggleLocation)) {
          // If location not selected, assume valid and select it
          qps[qp.locations] = [ ...locationIds, addLocation ];
        }
      }

      if (addItem) {
        if (!itemIds.includes(addItem)) {
          // If item not selected, assume valid and select it
          qps[qp.items] = [ ...itemIds, addItem ];
        }
        if (setItemDates || copyDefaultDates || defaultDates && !itemDates[addItem]) {
          const item = this._store.peekRecord('item', addItem);
          const start = item.get('temporalStartDate');
          const end = item.get('temporalEndDate');
          qps[qp.itemDates] = {
            ...itemDates,
            [addItem]: (setItemDates || defaultDates).map(mapToDates(start, end)),
          };
        }
      }

      if (addModelRun) {
        if (!modelRunIds.includes(addModelRun)) {
          // If model run not selected, assume valid and select it
          qps[qp.modelRuns] = [ ...modelRunIds, addModelRun ];
        }
        if (setModelRunDates || copyDefaultDates || defaultDates && !modelRunDates[addModelRun]) {
          const mr = this._store.peekRecord('model-run', addModelRun);
          const start = mr.get('temporalStartDate') || mr.get('item.temporalStartDate');
          const end = mr.get('temporalEndDate') || mr.get('item.temporalEndDate');
          qps[qp.modelRunDates] = {
            ...modelRunDates,
            [addModelRun]: (setModelRunDates || defaultDates).map(mapToDates(start, end)),
          };
        }
      }

      // Join any given query params
      Object.assign(qps, queryParams || {});
      // Clear any empty arrays
      [ qp.locations, qp.items, qp.modelRuns, qp.itemDates, qp.modelRunDates ]
        .forEach(qp => empty(qps, qp));
      // Generate transition/link
      return this.routeState.linkTo(route || routeName, { query: qps });
    };
  }

  /****************************** ^ Interface ^ *******************************/

  @service('router') _router;
  @service('routing') _routing;
  @service('store') _store;
  @service('time-series/params') _timeSeriesParams;

  _routeName = 'time-series';

  @tracked _transition;

  constructor(owner, args) {
    super(...arguments);
    this._router.on('routeWillChange', transition => this._onRouteChange(transition));
  }

  /**
   * @param transition
   * @private
   */
  _onRouteChange(transition) {
    const routeInfo = routeInfoOfName(transition.to, this._routeName);
    if (routeInfo) {
      this._transition = transition;
      this._timeSeriesParams.set('params',
        this.routeState.deserializeAllQueryParams(transition.to)
      );
    }
  }
}
